import React, { useState, useEffect } from 'react';
import '../styles/FriendsListModal.css';
import { 
  getFriendsList, 
  getPendingFriendRequests, 
  getSentFriendRequests,
  acceptFriendRequest,
  rejectFriendRequest,
  removeFriend,
  blockFriend,
  unblockFriend,
  searchUsersByWiinIdOrUsername,
  sendFriendRequest
} from '../services/friendService';
import { Friend } from '../types/friend';
import { FriendRequest } from '../types/friendRequest';
import { User } from '../services/userService';
import { useAuth } from '../context/AuthContext';

interface FriendsListModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserUsername: string;
  currentUserWiinId: string;
}

const FriendsListModal: React.FC<FriendsListModalProps> = ({
  isOpen,
  onClose,
  currentUserUsername,
  currentUserWiinId
}) => {
  const { currentUser } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingRequests, setPendingRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    if (isOpen && currentUser) {
      loadFriendsData();
    }
  }, [isOpen, currentUser]);

  const loadFriendsData = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const [friendsList, incoming, outgoing] = await Promise.all([
        getFriendsList(currentUser.uid),
        getPendingFriendRequests(currentUser.uid),
        getSentFriendRequests(currentUser.uid)
      ]);
      
      setFriends(friendsList);
      setPendingRequests(incoming);
      setSentRequests(outgoing);
    } catch (error) {
      console.error('Error loading friends data:', error);
      showMessage('Fejl ved indlæsning af venner');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!currentUser || !searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const results = await searchUsersByWiinIdOrUsername(searchTerm, currentUser.uid);
      setSearchResults(results);
      
      if (results.length === 0) {
        showMessage('Ingen brugere fundet');
      }
    } catch (error) {
      console.error('Error searching users:', error);
      showMessage('Fejl ved søgning');
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (user: User) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const receiverUsername = user.brugernavn || `${user.fornavn} ${user.efternavn}`;
      
      await sendFriendRequest(
        currentUser.uid,
        currentUserUsername,
        currentUserWiinId,
        user.uid,
        receiverUsername
      );
      
      showMessage('Venneanmodning sendt!');
      setSearchResults([]);
      setSearchTerm('');
      setShowSearch(false);
      await loadFriendsData();
    } catch (error: any) {
      console.error('Error sending friend request:', error);
      showMessage(error.message || 'Fejl ved afsendelse af anmodning');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      setLoading(true);
      await acceptFriendRequest(requestId);
      showMessage('Venneanmodning accepteret!');
      await loadFriendsData();
    } catch (error) {
      console.error('Error accepting request:', error);
      showMessage('Fejl ved accept af anmodning');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      setLoading(true);
      await rejectFriendRequest(requestId);
      showMessage('Venneanmodning afvist');
      await loadFriendsData();
    } catch (error) {
      console.error('Error rejecting request:', error);
      showMessage('Fejl ved afvisning af anmodning');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFriend = async (friendId: string, friendName: string) => {
    if (!currentUser) return;
    
    const confirmed = window.confirm(`Vil du fjerne ${friendName} fra dine venner?`);
    if (!confirmed) return;
    
    try {
      setLoading(true);
      await removeFriend(currentUser.uid, friendId);
      showMessage('Ven fjernet');
      await loadFriendsData();
    } catch (error) {
      console.error('Error removing friend:', error);
      showMessage('Fejl ved fjernelse af ven');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockFriend = async (friendId: string, friendName: string) => {
    if (!currentUser) return;
    
    const confirmed = window.confirm(`Vil du blokere ${friendName}?`);
    if (!confirmed) return;
    
    try {
      setLoading(true);
      await blockFriend(currentUser.uid, friendId);
      showMessage('Ven blokeret');
      await loadFriendsData();
    } catch (error) {
      console.error('Error blocking friend:', error);
      showMessage('Fejl ved blokering');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockFriend = async (friendId: string) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      await unblockFriend(currentUser.uid, friendId);
      showMessage('Blokering fjernet');
      await loadFriendsData();
    } catch (error) {
      console.error('Error unblocking friend:', error);
      showMessage('Fejl ved fjernelse af blokering');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  if (!isOpen) return null;

  const activeFriends = friends.filter(f => f.status === 'active');
  const blockedFriends = friends.filter(f => f.status === 'blocked');

  return (
    <div className="friends-modal-overlay" onClick={onClose}>
      <div className="friends-modal" onClick={(e) => e.stopPropagation()}>
        <div className="friends-modal-header">
          <h2>Venner</h2>
          <button className="friends-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Search Section */}
        <div className="friends-search-section">
          {!showSearch ? (
            <button 
              className="friends-add-button"
              onClick={() => setShowSearch(true)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Tilføj ven
            </button>
          ) : (
            <div className="friends-search-box">
              <input
                type="text"
                placeholder="Søg efter Wiin ID eller brugernavn"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="friends-search-input"
              />
              <button onClick={handleSearch} className="friends-search-btn">
                Søg
              </button>
              <button onClick={() => { setShowSearch(false); setSearchResults([]); setSearchTerm(''); }} className="friends-cancel-search">
                ✕
              </button>
            </div>
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="friends-search-results">
            <h3>Søgeresultater</h3>
            {searchResults.map((user) => (
              <div key={user.uid} className="friend-search-item">
                <div className="friend-search-info">
                  <span className="friend-search-name">
                    {user.brugernavn || `${user.fornavn} ${user.efternavn}`}
                  </span>
                  <span className="friend-search-wiinid">({user.Wiin_ID})</span>
                </div>
                <button
                  onClick={() => handleSendRequest(user)}
                  className="friend-add-icon-btn"
                  disabled={loading}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Message */}
        {message && (
          <div className="friends-message">
            {message}
          </div>
        )}

        {/* Friends List */}
        <div className="friends-modal-content">
          {loading && !friends.length && !pendingRequests.length ? (
            <div className="friends-loading">Indlæser...</div>
          ) : (
            <>
              {/* Pending Incoming Requests */}
              {pendingRequests.length > 0 && (
                <div className="friends-section">
                  <h3>Anmodninger</h3>
                  {pendingRequests.map((request) => (
                    <div key={request.id} className="friend-item">
                      <div className="friend-info">
                        <div className="friend-status-indicator pending"></div>
                        <div className="friend-details">
                          <span className="friend-name">{request.senderUsername}</span>
                          <span className="friend-wiinid">({request.senderWiinId})</span>
                        </div>
                      </div>
                      <div className="friend-actions">
                        <button
                          onClick={() => handleAcceptRequest(request.id)}
                          className="friend-action-btn accept"
                          disabled={loading}
                          title="Accepter"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => handleRejectRequest(request.id)}
                          className="friend-action-btn reject"
                          disabled={loading}
                          title="Afvis"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Sent Requests */}
              {sentRequests.length > 0 && (
                <div className="friends-section">
                  <h3>Afventer svar</h3>
                  {sentRequests.map((request) => (
                    <div key={request.id} className="friend-item">
                      <div className="friend-info">
                        <div className="friend-details">
                          <span className="friend-name">{request.receiverUsername}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Active Friends */}
              {activeFriends.length > 0 && (
                <div className="friends-section">
                  <h3>Venner</h3>
                  {activeFriends.map((friend) => (
                    <div key={friend.id} className="friend-item">
                      <div className="friend-info">
                        <div className="friend-details">
                          <span className="friend-name">{friend.username}</span>
                          <span className="friend-wiinid">({friend.wiinId})</span>
                        </div>
                      </div>
                      <div className="friend-actions">
                        <button
                          onClick={() => handleBlockFriend(friend.id, friend.username)}
                          className="friend-action-btn block"
                          disabled={loading}
                          title="Bloker"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => handleRemoveFriend(friend.id, friend.username)}
                          className="friend-action-btn remove"
                          disabled={loading}
                          title="Fjern ven"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6h18M8 6V4h8v2M10 11v6M14 11v6M5 6h14l-1 14H6L5 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Blocked Friends */}
              {blockedFriends.length > 0 && (
                <div className="friends-section">
                  <h3>Blokerede</h3>
                  {blockedFriends.map((friend) => (
                    <div key={friend.id} className="friend-item blocked">
                      <div className="friend-info">
                        <div className="friend-details">
                          <span className="friend-name">{friend.username}</span>
                          <span className="friend-wiinid">({friend.wiinId})</span>
                        </div>
                      </div>
                      <div className="friend-actions">
                        <button
                          onClick={() => handleUnblockFriend(friend.id)}
                          className="friend-action-btn unblock"
                          disabled={loading}
                          title="Fjern blokering"
                        >
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty State */}
              {friends.length === 0 && pendingRequests.length === 0 && sentRequests.length === 0 && (
                <div className="friends-empty">
                  <p>Du har ingen venner endnu</p>
                  <p className="friends-empty-hint">Brug søgefeltet ovenfor for at tilføje venner</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FriendsListModal;
